import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Avatar, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Tooltip, Divider, Paper, Pagination, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  useTheme, useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Person as PersonIcon, Phone as PhoneIcon, Email as EmailIcon, Visibility as ViewIcon,
  Refresh as RefreshIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import PatientDetailPanel from '../../components/PatientDetailPanel';
import PatientFormDialog from '../../components/PatientFormDialog';
import HealthcarePanel from '../../components/HealthcarePanel';

interface Patient {
  id: string;
  contact_id?: string;
  full_name: string;
  social_name?: string;
  cpf?: string;
  rg?: string;
  birth_date?: string;
  gender?: string;
  marital_status?: string;
  profession?: string;
  email?: string;
  phone?: string;
  phone_secondary?: string;
  whatsapp?: string;
  blood_type?: string;
  status: string;
  insurance_plan?: string;
  insurance_number?: string;
  insurance_validity?: string;
  allergies?: string[];
  chronic_conditions?: string[];
  medications?: string[];
  medical_notes?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relation?: string;
  guardian_name?: string;
  guardian_phone?: string;
  guardian_relation?: string;
  address_street?: string;
  address_number?: string;
  address_complement?: string;
  address_neighborhood?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  total_visits: number;
  total_spent: number;
  last_visit_date?: string;
  first_visit_date?: string;
  tags?: string[];
  avatar_url?: string;
  created_at: string;
  clinic_insurances?: { id: string; name: string; type: string };
}

const Patients: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  /** `null` no dialog = cadastro novo; preenchido = edição */
  const [dialogPatient, setDialogPatient] = useState<Patient | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [hcPanelOpen, setHcPanelOpen] = useState(false);
  const [hcPanelPatient, setHcPanelPatient] = useState<Patient | null>(null);
  const limit = 20;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletOrDown = useMediaQuery(theme.breakpoints.down('md'));

  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page, limit };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const response = await axios.get(getApiUrl('/clinic/patients'), { params });
      if (response.data.success) {
        setPatients(response.data.data || []);
        setTotal(response.data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching patients:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchPatients(); }, [fetchPatients]);


  const handleEdit = (patient: Patient) => {
    setDialogPatient(patient);
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja remover este paciente?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/patients/${id}`));
      setSnackbar({ open: true, message: 'Paciente removido!', severity: 'success' });
      fetchPatients();
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao remover paciente', severity: 'error' });
    }
  };

  const handleViewDetails = (patient: Patient) => {
    setSelectedPatient(patient);
    setDetailOpen(true);
  };

  const handleOpenHealthcarePanel = (patient: Patient) => {
    if (!patient.contact_id) {
      setSnackbar({ open: true, message: 'Paciente sem contato vinculado para abrir o Healthcare Panel', severity: 'error' });
      return;
    }
    setHcPanelPatient(patient);
    setHcPanelOpen(true);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR'); } catch { return '-'; }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

  const calculateAge = (birthDate?: string) => {
    if (!birthDate) return '';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return `${age} anos`;
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, backgroundColor: 'transparent', minHeight: '100vh', pb: { xs: 10, sm: 3 } }}>

      {/* ── Header ── */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: { xs: 2, md: 3 },
        gap: 2,
      }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, color: '#1e1b4b', lineHeight: 1.2 }}>
            Gestão de Pacientes
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>{total} pacientes cadastrados</Typography>
        </Box>
        <Button
          variant="contained"
          fullWidth={isMobile}
          startIcon={<AddIcon />}
          onClick={() => { setDialogPatient(null); setDialogOpen(true); }}
          sx={{
            bgcolor: '#14b8a6',
            '&:hover': { bgcolor: '#0d9488' },
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            py: { xs: 1.25, sm: 0.75 },
            flexShrink: 0,
          }}
        >
          Novo Paciente
        </Button>
      </Box>

      {/* ── Filters ── */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1.5, sm: 2 },
        mb: { xs: 2, md: 3 },
        alignItems: { xs: 'stretch', sm: 'center' },
      }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar paciente..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          sx={{ flex: { sm: 1 }, minWidth: 0, maxWidth: { sm: 420 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" fullWidth={isMobile} sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="active">Ativo</MenuItem>
              <MenuItem value="inactive">Inativo</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Atualizar">
            <IconButton onClick={fetchPatients} sx={{ flexShrink: 0 }} aria-label="Atualizar lista">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Lista (cards no mobile/tablet, tabela no desktop) ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: '#14b8a6' }} />
        </Box>
      ) : (
        <>
          {isTabletOrDown ? (
            <Stack spacing={1.5} sx={{ width: '100%' }}>
              {patients.map((patient) => (
                <Card
                  key={patient.id}
                  elevation={0}
                  onClick={() => handleViewDetails(patient)}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    cursor: 'pointer',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    '&:active': { transform: 'scale(0.99)' },
                  }}
                >
                  <CardContent sx={{ py: 1.75, px: 2, '&:last-child': { pb: 1.75 } }}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Avatar
                        src={patient.avatar_url || undefined}
                        sx={{ bgcolor: '#14B8A6', width: 48, height: 48, fontSize: 18, flexShrink: 0 }}
                      >
                        {patient.full_name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e1b4b', lineHeight: 1.3 }}>
                          {patient.full_name}
                        </Typography>
                        {patient.cpf && (
                          <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block' }}>{patient.cpf}</Typography>
                        )}
                        <Typography variant="body2" sx={{ color: '#374151', mt: 0.5, wordBreak: 'break-word' }}>
                          {patient.phone || '—'}
                        </Typography>
                        {patient.email && (
                          <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 0.25, wordBreak: 'break-all' }}>
                            {patient.email}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mt: 1 }}>
                          <Chip label={patient.status === 'active' ? 'Ativo' : 'Inativo'} size="small"
                            sx={{ bgcolor: patient.status === 'active' ? '#d1fae5' : '#f3f4f6', color: patient.status === 'active' ? '#059669' : '#6b7280', fontWeight: 600, height: 24 }} />
                          <Typography variant="caption" sx={{ color: '#64748b' }}>{calculateAge(patient.birth_date)}</Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>·</Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {patient.clinic_insurances?.name || patient.insurance_plan || 'Particular'}
                          </Typography>
                          <Chip label={`${patient.total_visits} visitas`} size="small" variant="outlined" sx={{ borderColor: '#99f6e4', color: '#0d9488', fontWeight: 600, height: 24 }} />
                        </Box>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 1.25 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Ver detalhes"><IconButton size="medium" onClick={() => handleViewDetails(patient)} aria-label="Ver detalhes"><ViewIcon /></IconButton></Tooltip>
                      <Tooltip title="Healthcare Panel"><IconButton size="medium" onClick={() => handleOpenHealthcarePanel(patient)} aria-label="Healthcare"><HospitalIcon sx={{ color: '#14b8a6' }} /></IconButton></Tooltip>
                      <Tooltip title="Editar"><IconButton size="medium" onClick={() => handleEdit(patient)} aria-label="Editar"><EditIcon /></IconButton></Tooltip>
                      <Tooltip title="Excluir"><IconButton size="medium" onClick={() => handleDelete(patient.id)} aria-label="Excluir"><DeleteIcon sx={{ color: '#ef4444' }} /></IconButton></Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              {patients.length === 0 && (
                <Paper sx={{ py: 6, textAlign: 'center', borderRadius: 2, border: '1px dashed #e5e7eb' }}>
                  <PersonIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                  <Typography variant="body1" sx={{ color: '#9ca3af' }}>Nenhum paciente encontrado</Typography>
                </Paper>
              )}
            </Stack>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f9fafb' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Paciente</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Contato</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Idade</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Convênio</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Visitas</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#374151' }} align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patients.map((patient) => (
                    <TableRow key={patient.id} hover sx={{ '&:hover': { bgcolor: '#f0fdfa' }, cursor: 'pointer' }}
                      onClick={() => handleViewDetails(patient)}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar src={patient.avatar_url || undefined}
                            sx={{ bgcolor: '#14B8A6', width: 36, height: 36, fontSize: 14 }}>
                            {patient.full_name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e1b4b' }}>{patient.full_name}</Typography>
                            {patient.cpf && <Typography variant="caption" sx={{ color: '#9ca3af' }}>{patient.cpf}</Typography>}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#374151' }}>{patient.phone || '-'}</Typography>
                        <Typography variant="caption" sx={{ color: '#9ca3af' }}>{patient.email || ''}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#374151' }}>{calculateAge(patient.birth_date)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#374151' }}>{patient.clinic_insurances?.name || patient.insurance_plan || 'Particular'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#14b8a6' }}>{patient.total_visits}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={patient.status === 'active' ? 'Ativo' : 'Inativo'} size="small"
                          sx={{ bgcolor: patient.status === 'active' ? '#d1fae5' : '#f3f4f6', color: patient.status === 'active' ? '#059669' : '#6b7280', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Ver detalhes"><IconButton size="small" onClick={() => handleViewDetails(patient)}><ViewIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                        <Tooltip title="Healthcare Panel">
                          <IconButton size="small" onClick={() => handleOpenHealthcarePanel(patient)}>
                            <HospitalIcon sx={{ fontSize: 18, color: '#14b8a6' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEdit(patient)}><EditIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                        <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDelete(patient.id)}><DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {patients.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                        <PersonIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
                        <Typography variant="body1" sx={{ color: '#9ca3af' }}>Nenhum paciente encontrado</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {total > limit && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, overflowX: 'auto', pb: 1 }}>
              <Pagination
                count={Math.ceil(total / limit)}
                page={page}
                onChange={(_, v) => setPage(v)}
                size={isMobile ? 'small' : 'medium'}
                siblingCount={isMobile ? 0 : 1}
                sx={{ '& .MuiPaginationItem-root': { '&.Mui-selected': { bgcolor: '#14b8a6', color: '#fff' } } }}
              />
            </Box>
          )}
        </>
      )}

      <PatientFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        patientToEdit={dialogPatient}
        onSaved={({ mode }) => {
          fetchPatients();
          setSnackbar({ open: true, message: mode === 'create' ? 'Paciente cadastrado com sucesso!' : 'Paciente atualizado com sucesso!', severity: 'success' });
        }}
        onError={(message) => setSnackbar({ open: true, message, severity: 'error' })}
      />

      {/* ============================================================
          PATIENT DETAIL DIALOG — PAINEL COMPLETO
      ============================================================ */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: isMobile
            ? { m: 0, borderRadius: 0, maxHeight: '100%', height: '100%', display: 'flex', flexDirection: 'column' }
            : { borderRadius: 3, overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
        }}
      >

        {selectedPatient && (
          <PatientDetailPanel
            patientId={selectedPatient.id}
            initialPatient={selectedPatient}
            onClose={() => setDetailOpen(false)}
            onEdit={(p) => { setDetailOpen(false); handleEdit(p); }}
            onPatientUpdated={(updated) => {
              setSelectedPatient(updated);
              setPatients(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
            }}
          />
        )}
      </Dialog>

      <HealthcarePanel
        open={hcPanelOpen}
        onClose={() => setHcPanelOpen(false)}
        contactId={hcPanelPatient?.contact_id || null}
        contactName={hcPanelPatient?.full_name}
        contactPhone={hcPanelPatient?.whatsapp || hcPanelPatient?.phone}
        contactEmail={hcPanelPatient?.email}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
        sx={{ bottom: { xs: 24, sm: 24 } }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%', maxWidth: { xs: 'calc(100vw - 32px)', sm: 400 } }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Patients;
